import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Grid, message, Modal } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import { type MobilityDataFragment } from '../../../api/fragments';
import {
    DeleteMobilityDocument,
    type DeleteMobilityMutation,
    type DeleteMobilityMutationVariables,
} from '../../../api/mutations/deleteMobility';
import {
    UpdateMobilityAdditionalInfoDocument,
    type UpdateMobilityAdditionalInfoMutation,
    type UpdateMobilityAdditionalInfoMutationVariables,
} from '../../../api/mutations/updateMobilityAdditionalInfo';
import {
    UpdateMobilityAddonDocument,
    type UpdateMobilityAddonMutation,
    type UpdateMobilityAddonMutationVariables,
} from '../../../api/mutations/updateMobilityAddon';
import { type MobilityAdditionalInfoSetting, MobilityKind } from '../../../api/types';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import { cleanupMobilityAddOn } from '../AddMobilityPage';
import { UpdateMobilityForm, type UpdateMobilityFormValues } from './MobilityForm';
import useMobilityFormValidator from './MobilityForm/useMobilityFormValidator';

export type MobilityDetailsInnerPageProps = { mobility: MobilityDataFragment };

const MobilityDetailsInnerPage = ({ mobility }: MobilityDetailsInnerPageProps) => {
    const { t } = useTranslation(['mobilityDetails']);
    const screens = Grid.useBreakpoint();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: mobility.versioning,
        moduleId: mobility.moduleId,
    });

    const { hasDeletePermission, hasUpdatePermission } = useMemo(() => {
        if (mobility.module.__typename !== 'MobilityModule') {
            return {
                hasDeletePermission: false,
                hasUpdatePermission: false,
            };
        }

        return {
            hasDeletePermission: hasPermissions(mobility.module.permissions, [permissionKind.deleteMobility]),
            hasUpdatePermission: hasPermissions(mobility.module.permissions, [permissionKind.updateMobility]),
        };
    }, [mobility]);

    const initialValues = useMemo<UpdateMobilityFormValues>(() => {
        const typename = mobility.__typename;
        switch (typename) {
            case 'MobilityAddon': {
                return {
                    id: mobility.id,
                    moduleId: mobility.moduleId,
                    kind: MobilityKind.Addon,
                    isActive: mobility.isActive,
                    isChargeable: mobility.isChargeable,
                    options: mobility.options,
                    order: mobility.order,
                    referenceName: mobility.referenceName,
                    title: mobility.title,
                    stockIds: mobility.stockIds,
                };
            }

            case 'MobilityAdditionalInfo': {
                return {
                    id: mobility.id,
                    moduleId: mobility.moduleId,
                    kind: MobilityKind.AdditionalInfo,
                    isActive: mobility.isActive,
                    detail: mobility.detail,
                    order: mobility.order,
                    title: mobility.title,
                    stockIds: mobility.stockIds,
                };
            }

            default:
                throw new Error(`invalid mobility kind: ${typename}`);
        }
    }, [mobility]);

    const validate = useMobilityFormValidator();

    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const onSubmit = useHandleError<UpdateMobilityFormValues>(
        async values => {
            const { id, moduleId, ...settings } = values;
            // submitting message
            message.loading({
                content: t('mobilityDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            if (settings.kind === MobilityKind.Addon) {
                await apolloClient
                    .mutate<UpdateMobilityAddonMutation, UpdateMobilityAddonMutationVariables>({
                        mutation: UpdateMobilityAddonDocument,
                        variables: {
                            id,
                            setting: cleanupMobilityAddOn(settings),
                        },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });
            } else if (settings.kind === MobilityKind.AdditionalInfo) {
                await apolloClient
                    .mutate<UpdateMobilityAdditionalInfoMutation, UpdateMobilityAdditionalInfoMutationVariables>({
                        mutation: UpdateMobilityAdditionalInfoDocument,
                        variables: {
                            id,
                            setting: omit(['options', 'kind'], settings) as MobilityAdditionalInfoSetting,
                        },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });
            }

            // inform about success
            message.success({
                content: t('mobilityDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(`/admin/mobilityoptions`);
        },
        [apolloClient, navigate, t]
    );

    const deleteMobility = useCallback(() => {
        Modal.confirm({
            title: t('mobilityDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('mobilityDetails:deleteModal.content'),
            okText: t('mobilityDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('mobilityDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('mobilityDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteMobilityMutation, DeleteMobilityMutationVariables>({
                            mutation: DeleteMobilityDocument,
                            variables: { id: mobility.id },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('mobilityDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/mobilityoptions');
                } catch (error) {
                    if (error?.$root) {
                        message.error(error?.$root);
                    }
                }
            },
        });
    }, [apolloClient, mobility.id, navigate, t]);

    return (
        <ConsolePageWithHeader
            extra={
                hasDeletePermission && (
                    <Button onClick={deleteMobility} danger>
                        {t('mobilityDetails:actions.delete')}
                    </Button>
                )
            }
            footer={
                hasUpdatePermission && [
                    <Button key="submit" form="mobilityForm" htmlType="submit" type="primary">
                        {t('mobilityDetails:actions.update')}
                    </Button>,
                ]
            }
            onBack={() => navigate('/admin/mobilityoptions')}
            title={
                screens.md
                    ? t('mobilityDetails:title', { name: initialValues.title.defaultValue })
                    : initialValues.title.defaultValue
            }
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="mobilityForm" name="mobilityForm" onSubmitCapture={handleSubmit}>
                        <UpdateMobilityForm
                            disabled={!hasUpdatePermission}
                            module={mobility.module}
                            offset={offset}
                            updated={updated}
                        />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default MobilityDetailsInnerPage;
